// client/src/App.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

import Home from './pages/Home';
import ProjectsListPage from './pages/ProjectsListPage';
import ProjectEstimatePage from './pages/EstimatePage';
import Profile from './pages/Profile';
import AuthPage from './pages/AuthPage';
import NotFound from './pages/NotFound';
import ResetPassword from './pages/ResetPassword';
import AcceptShare from './pages/AcceptShare';
import AcceptOrg from './pages/AcceptOrg';
import AdminConsole from './pages/AdminConsole';

import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import { useAuth } from './contexts/AuthContext';
import { useOrg } from './contexts/OrgContext';
import DesignAssistant from './pages/DesignAssistant';
import { LG_MEDIA_QUERY, useIsLg } from './hooks/useIsLg';
import Vendors from './pages/Vendors';
import CommunityBuilds from './pages/CommunityBuilds';
import CommunityBuildDetail from './pages/CommunityBuildDetail';

const SIDEBAR_STORAGE_KEY = 'ai.sidebar.open';

export default function App() {
  const location = useLocation();
  const { session } = useAuth();
  const { loading: orgLoading, error: orgError, refresh: refreshOrg, permissions } = useOrg();
  const loading = session === undefined;
  const sidebarRef = useRef(null);
  const isDesktop = useIsLg();

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) {
      try {
        return Boolean(JSON.parse(stored));
      } catch (error) {
        console.warn('Unable to parse sidebar preference', error);
      }
    }
    return window.matchMedia(LG_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!session) {
      setSidebarOpen(false);
      return;
    }

    if (isDesktop) {
      const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored !== null) {
        try {
          setSidebarOpen(Boolean(JSON.parse(stored)));
          return;
        } catch (error) {
          console.warn('Unable to parse sidebar preference', error);
        }
      }
      setSidebarOpen(true);
    } else {
      setSidebarOpen(false);
    }
  }, [isDesktop, session]);

  useEffect(() => {
    if (typeof window === 'undefined' || !isDesktop || !session) return;
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(Boolean(sidebarOpen)));
  }, [sidebarOpen, isDesktop, session]);

  useEffect(() => {
    if (!session || isDesktop || !sidebarOpen) return;
    const node = sidebarRef.current;
    if (!node) return;

    const selectors = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = Array.from(node.querySelectorAll(selectors)).filter(
      (el) => el.getAttribute('tabindex') !== '-1'
    );

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const handleKeyDown = (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      if (event.key === 'Escape') {
        setSidebarOpen(false);
      }
    };

    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    first.focus();
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      body.style.overflow = previousOverflow;
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [isDesktop, sidebarOpen, session]);

  useEffect(() => {
    if (!session || isDesktop) return;
    setSidebarOpen(false);
  }, [location.pathname, isDesktop, session]);

  const hideShell = location.pathname === '/reset' || location.pathname.startsWith('/accept');
  const title = useMemo(() => deriveTitleFromPath(location.pathname), [location.pathname]);
  const sidebarId = 'app-sidebar';
  const showHamburger = session ? !isDesktop : false;

  const routes = (
    <Routes>
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/accept/share" element={<AcceptShare />} />
      <Route path="/accept/org" element={<AcceptOrg />} />
      <Route
        path="/"
        element={
          session
            ? permissions?.canAccessDashboard === false && permissions?.role !== 'admin'
              ? permissions?.canAccessDesign === false && permissions?.role !== 'admin'
                ? <Navigate to="/profile" replace />
                : <Navigate to="/design-assistant" replace />
              : <Home />
            : <AuthPage />
        }
      />
      <Route
        path="/design-assistant"
        element={
          session
            ? permissions?.canAccessDesign === false && permissions?.role !== 'admin'
              ? <Navigate to="/" replace />
              : <DesignAssistant />
            : <AuthPage />
        }
      />
      <Route
        path="/projects"
        element={
          session
            ? permissions?.canAccessProjects === false && permissions?.role !== 'admin'
              ? <Navigate to="/" replace />
              : <ProjectsListPage />
            : <AuthPage />
        }
      />
      <Route
        path="/projects/:projectId/estimate"
        element={
          session
            ? permissions?.canAccessProjects === false && permissions?.role !== 'admin'
              ? <Navigate to="/" replace />
              : <ProjectEstimatePage />
            : <AuthPage />
        }
      />
      <Route
        path="/estimate/:projectId?"
        element={
          session
            ? permissions?.canAccessEstimates === false && permissions?.role !== 'admin'
              ? <Navigate to="/" replace />
              : <ProjectEstimatePage />
            : <AuthPage />
        }
      />
      <Route path="/profile" element={session ? <Profile /> : <AuthPage />} />
      <Route
        path="/design"
        element={<Navigate to="/design-assistant" replace />}
      />
      <Route
        path="/community-builds"
        element={
          session
            ? <CommunityBuilds />
            : <AuthPage />
        }
      />
      <Route
        path="/community-builds/:buildId"
        element={
          session
            ? <CommunityBuildDetail />
            : <AuthPage />
        }
      />
      <Route
        path="/vendors"
        element={
          session
            ? <Vendors />
            : <AuthPage />
        }
      />
      <Route
        path="/admin"
        element={
          session
            ? permissions?.canAccessAdmin || permissions?.role === 'admin'
              ? <AdminConsole />
              : <Navigate to="/" replace />
            : <AuthPage />
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  if (loading) {
    return <p className="p-5 text-sm text-zinc-200">Loading...</p>;
  }

  if (session && orgLoading) {
    return <p className="p-5 text-sm text-zinc-200">Loading workspace...</p>;
  }

  if (!session || hideShell) {
    return (
      <div className="app-shell text-zinc-100">
        <main className="mx-auto w-full max-w-[960px] px-4 py-8 sm:px-8">{routes}</main>
      </div>
    );
  }

  const handleHamburgerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const workspaceBanner = session && orgError ? (
    <div className="glass-card mb-4 border border-amber-400/30 bg-gradient-to-r from-amber-400/10 via-red-500/5 to-transparent px-4 py-3 text-sm text-amber-100 shadow-[0_20px_80px_-50px_rgba(246,196,82,0.8)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="font-medium">Workspace status: {orgError}</span>
        <button
          type="button"
          onClick={refreshOrg}
          className="glass-ghost px-3 py-2 text-xs font-semibold text-amber-100"
        >
          Try again
        </button>
      </div>
    </div>
  ) : null;

  const contentOffset = isDesktop ? (sidebarOpen ? 'lg:ml-72' : 'lg:ml-[72px]') : '';

  return (
    <div className="app-shell text-zinc-100">
      <div className="flex min-h-screen">
        {isDesktop ? (
          <Sidebar
            id={sidebarId}
            ref={sidebarRef}
            open={sidebarOpen}
            setOpen={setSidebarOpen}
          />
        ) : (
          <>
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                role="presentation"
                onClick={() => setSidebarOpen(false)}
                onPointerDown={() => setSidebarOpen(false)}
              />
            )}
            {sidebarOpen && (
              <Sidebar
                id={sidebarId}
                ref={sidebarRef}
                open={sidebarOpen}
                setOpen={setSidebarOpen}
                mobile
              />
            )}
          </>
        )}

        <div className={`flex flex-1 flex-col transition-all duration-200 ${contentOffset}`}>
          <Topbar
            onHamburger={handleHamburgerToggle}
            title={title}
            sidebarOpen={sidebarOpen}
            sidebarId={sidebarId}
            showHamburger={showHamburger}
          />
          <main className="flex-1 overflow-x-hidden px-4 py-6 lg:px-8 lg:py-8">
            <div className="mx-auto w-full max-w-[1600px]">
              {workspaceBanner}
              {routes}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function deriveTitleFromPath(pathname) {
  if (!pathname || pathname === '/') return 'Dashboard';
  if (pathname.startsWith('/design-assistant')) return 'Design Assistant';
  if (pathname.startsWith('/design')) return 'Design Assistant';
  if (pathname.startsWith('/projects') && pathname.includes('/estimate')) return 'Estimate';
  if (pathname.startsWith('/projects')) return 'Projects';
  if (pathname.startsWith('/profile')) return 'Profile';
  if (pathname.startsWith('/estimate')) return 'Estimate';
  if (pathname.startsWith('/vendors')) return 'Vendors';
  if (pathname.startsWith('/community-builds')) return 'Community Builds';
  if (pathname.startsWith('/auth')) return 'Sign In';
  if (pathname.startsWith('/reset')) return 'Reset Password';
  if (pathname.startsWith('/accept')) return 'Accept Invitation';

  const segments = pathname.split('/').filter(Boolean);
  if (!segments.length) return 'Dashboard';
  const last = segments[segments.length - 1];
  return last
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
